import {lazy, Suspense, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Navigate, Route, Routes} from 'react-router-dom';
import PageNotFound from './PageNotFound';
import {Container, Spinner} from 'reactstrap';
import {useAuthStateManager} from './hooks/useAuthStateManager';
import Layout from './components/layout/Layout';
import pages, {Page} from './pages';
import {RoutePaths} from './RoutePaths';
import GlobalFunctionWrapper from './GlobalFunctionWrapper';
import RouteRedirect from './components/utility/RouteRedirect';
import {
    AssistantContextProvider,
    AssistantIntegration,
    AssistantIntegrationProvider,
    AssistantPanelProvider
} from './assistant';
import {useCreateAuthenticatedFetch} from './api/useCreateAuthenticatedFetch';
import baseUrl from './api/baseUrl';
import {useQueryClient} from '@tanstack/react-query';
import {refreshAfterAssistantTool} from './api/assistantToolRefresh';
import RoleNames from './lib/RoleNames';
import useAssistantStarterPrompts from './hooks/useAssistantStarterPrompts';

const MsAuthPage = lazy(() => import('./components/social_logins/MsAuthPage'));

const Fallback = () => (
    <div className="d-flex flex-fill align-items-center justify-content-center">
        <Spinner color="primary" />
    </div>
);

function AppRoutes() {
    const {
        isAuthorizedForResource,
        getAccessToken,
        personId,
        isAuthenticated,
        hasCustomerLogin
    } = useAuthStateManager();
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const assistantIntegration = useMemo<AssistantIntegration>(
        () => ({
            authenticatedFetch,
            accessTokenFactory: getAccessToken,
            baseUrl,
            userStorageKey: personId,
            // Smart refresh: assistant tool edits invalidate the matching
            // caches so open pages re-fetch (see api/assistantToolRefresh.ts).
            onToolCallCompleted: toolName =>
                refreshAfterAssistantTool(queryClient, toolName),
            // The assistant's navigate_to tool: go there, keep the conversation.
            onNavigate: path => navigate(path),
            // Suggestions for an empty conversation, from the page and the role.
            useStarterPrompts: useAssistantStarterPrompts
        }),
        [authenticatedFetch, getAccessToken, personId, queryClient, navigate]
    );

    const renderElement = (page: Page) => {
        if (!isAuthorizedForResource(page)) {
            // A signed-in user with no company yet (a fresh sign-up, or a colleague
            // nobody has added) belongs in the company wizard, which also explains
            // how to join an existing workspace. Not the marketing home page.
            const needsCompany =
                isAuthenticated &&
                !hasCustomerLogin &&
                (page.roles ?? []).includes(RoleNames.CustomerUser);
            return (
                <Navigate
                    to={needsCompany ? RoutePaths.company.register() : RoutePaths.home()}
                    replace
                />
            );
        }

        // A moved route: no component, just send the visitor to the new path.
        if (page.redirectTo) {
            return <RouteRedirect to={page.redirectTo} />;
        }

        const PageComponent = page.component;
        if (!PageComponent) {
            return <PageNotFound />;
        }

        if (page.wrapper) {
            return (
                <GlobalFunctionWrapper>
                    <page.wrapper>
                        <PageComponent />
                    </page.wrapper>
                </GlobalFunctionWrapper>
            );
        }

        if (page.fullBleed) {
            return (
                <GlobalFunctionWrapper>
                    <PageComponent />
                </GlobalFunctionWrapper>
            );
        }

        return (
            <GlobalFunctionWrapper>
                <Container
                    className="py-4 px-3 mx-auto"
                    fluid={page.fluidContainer}
                >
                    <PageComponent />
                </Container>
            </GlobalFunctionWrapper>
        );
    };

    const renderRoute = (page: Page) => {
        const element = renderElement(page);

        return (
            <Route key={page.path} path={page.path} element={element}>
                {page.children?.map(childPage =>
                    renderRoute({
                        ...childPage,
                        // Combine parent and child paths for the key
                        path: `${page.path}/${childPage.path}`.replace(
                            /\/\//g,
                            '/'
                        )
                    })
                )}
            </Route>
        );
    };

    return (
        <Suspense fallback={<Fallback />}>
            <AssistantIntegrationProvider integration={assistantIntegration}>
                <AssistantContextProvider>
                    <AssistantPanelProvider>
                        <Routes>
                            <Route path="/" element={<Layout />}>
                                {pages.map(page => renderRoute(page))}

                                <Route
                                    path={RoutePaths.msAuth()}
                                    element={
                                        <GlobalFunctionWrapper>
                                            <MsAuthPage />
                                        </GlobalFunctionWrapper>
                                    }
                                />

                                <Route path="*" element={<PageNotFound />} />
                            </Route>
                        </Routes>
                    </AssistantPanelProvider>
                </AssistantContextProvider>
            </AssistantIntegrationProvider>
        </Suspense>
    );
}

export default AppRoutes;
